import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  GitBranch,
  Search,
  Filter,
  Lock,
  Copy,
  BarChart3,
  Play,
  Eye,
  EyeOff,
  Loader2,
  Star
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { BeltIndicator } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import ScenarioBuilder from './ScenarioBuilder';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Scenario categories
const SCENARIO_CATEGORIES = [
  { value: 'self_defense', label: 'Self-Defense' },
  { value: 'etiquette', label: 'Etiquette' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'technique', label: 'Technique Application' },
  { value: 'conflict_resolution', label: 'Conflict Resolution' },
];

// Category icons/emojis
const CATEGORY_ICONS = {
  self_defense: '\\uD83E\\uDD4A',
  etiquette: '\\uD83C\\uDF8C',
  tournament: '\\uD83C\\uDFC6',
  technique: '\\uD83E\\uDD4B',
  conflict_resolution: '\\uD83E\\uDD1D',
};

// Difficulty levels
const DIFFICULTY_LEVELS = [
  { value: 1, label: 'Beginner' },
  { value: 2, label: 'Elementary' },
  { value: 3, label: 'Intermediate' },
  { value: 4, label: 'Advanced' },
  { value: 5, label: 'Expert' },
];

// ============================================
// Scenario Form
// ============================================
const ScenarioForm = ({ scenario, programs, onSave, onCancel }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();

  const [formData, setFormData] = useState({
    title: scenario?.title || '',
    description: scenario?.description || '',
    category: scenario?.category || 'self_defense',
    difficulty: scenario?.difficulty || 1,
    cover_image_url: scenario?.cover_image_url || '',
    icon_emoji: scenario?.icon_emoji || CATEGORY_ICONS[scenario?.category || 'self_defense'] || '',
    required_belt: scenario?.required_belt || '',
    required_program_ids: scenario?.required_program_ids || [],
    base_points: scenario?.base_points || 50,
    optimal_bonus: scenario?.optimal_bonus || 50,
    is_active: scenario?.is_active ?? true,
    is_featured: scenario?.is_featured ?? false,
  });
  const [saving, setSaving] = useState(false);

  const handleProgramToggle = (programId) => {
    const current = formData.required_program_ids;
    if (current.includes(programId)) {
      setFormData({
        ...formData,
        required_program_ids: current.filter(id => id !== programId)
      });
    } else {
      setFormData({
        ...formData,
        required_program_ids: [...current, programId]
      });
    }
  };

  const handleCategoryChange = (category) => {
    setFormData({
      ...formData,
      category,
      icon_emoji: CATEGORY_ICONS[category] || formData.icon_emoji
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Walking Alone at Night"
          required
        />
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="A brief description of the scenario..."
        />
      </div>

      {/* Category & Difficulty */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category *</Label>
          <select
            id="category"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
            value={formData.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            {SCENARIO_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="difficulty">Difficulty</Label>
          <select
            id="difficulty"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
            value={formData.difficulty}
            onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) })}
          >
            {DIFFICULTY_LEVELS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Icon Emoji */}
      <div>
        <Label htmlFor="icon_emoji">Icon Emoji</Label>
        <Input
          id="icon_emoji"
          value={formData.icon_emoji}
          onChange={(e) => setFormData({ ...formData, icon_emoji: e.target.value })}
          placeholder="Enter an emoji"
          className="w-24"
        />
        <span className="ml-2 text-2xl">{formData.icon_emoji}</span>
      </div>

      {/* Points Configuration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="base_points">Base Points</Label>
          <Input
            id="base_points"
            type="number"
            min={0}
            value={formData.base_points}
            onChange={(e) => setFormData({ ...formData, base_points: parseInt(e.target.value) || 0 })}
          />
          <p className="text-xs text-gray-500 mt-1">Points for completing</p>
        </div>
        <div>
          <Label htmlFor="optimal_bonus">Optimal Bonus</Label>
          <Input
            id="optimal_bonus"
            type="number"
            min={0}
            value={formData.optimal_bonus}
            onChange={(e) => setFormData({ ...formData, optimal_bonus: parseInt(e.target.value) || 0 })}
          />
          <p className="text-xs text-gray-500 mt-1">Extra for optimal ending</p>
        </div>
      </div>

      {/* Access Control */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-2 flex items-center gap-2">
          <Lock className="h-4 w-4" /> Access Control
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="required_belt">Required Belt</Label>
            <select
              id="required_belt"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
              value={formData.required_belt}
              onChange={(e) => setFormData({ ...formData, required_belt: e.target.value })}
            >
              <option value="">No restriction</option>
              {beltOptions.map(belt => (
                <option key={belt.slug} value={belt.slug}>{belt.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Program Requirements */}
        {programs && programs.length > 0 && (
          <div className="mt-3">
            <Label>Required Programs (student must be in at least one)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {programs.map(program => (
                <button
                  key={program.id}
                  type="button"
                  onClick={() => handleProgramToggle(program.id)}
                  className={`px-3 py-1 text-sm rounded-full border ${
                    formData.required_program_ids.includes(program.id)
                      ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-300'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {program.title}
                </button>
              ))}
            </div>
            {formData.required_program_ids.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No program restriction (all students)</p>
            )}
          </div>
        )}
      </div>

      {/* Status Toggles */}
      <div className="flex items-center gap-6 border-t pt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">Active</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_featured}
            onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
            className="rounded"
          />
          <Star className={`h-4 w-4 ${formData.is_featured ? 'text-yellow-500' : 'text-gray-400'}`} />
          <span className="text-sm">Featured</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-1" /> Cancel
        </Button>
        <Button type="submit" disabled={saving || !formData.title}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {scenario ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};

// ============================================
// Stats Dialog
// ============================================
const StatsDialog = ({ scenario, open, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && scenario) {
      loadStats();
    }
  }, [open, scenario]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Statistics: {scenario?.title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : stats ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{stats.total_attempts}</p>
                <p className="text-xs text-blue-600">Total Attempts</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{stats.completed_attempts}</p>
                <p className="text-xs text-green-600">Completed</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-700">{stats.optimal_completions}</p>
                <p className="text-xs text-purple-600">Optimal Endings</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-700">{stats.average_choices}</p>
                <p className="text-xs text-orange-600">Avg Choices</p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <p className="text-sm font-medium mb-2">Completion Rate</p>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${stats.completion_rate}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{stats.completion_rate.toFixed(1)}%</p>
            </div>

            {stats.ending_distribution && Object.keys(stats.ending_distribution).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Ending Distribution</p>
                <div className="space-y-1">
                  {Object.entries(stats.ending_distribution).map(([ending, count]) => (
                    <div key={ending} className="flex justify-between text-sm">
                      <span className="capitalize">{ending}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No statistics available</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Main Component
// ============================================
const ScenarioManager = () => {
  const [scenarios, setScenarios] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [statsScenario, setStatsScenario] = useState(null);
  const [treeEditScenario, setTreeEditScenario] = useState(null); // For tree editor
  const { getBeltOptions } = useBelts();

  const loadScenarios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (showInactive) params.append('include_inactive', 'true');

      const response = await fetch(`${API_BASE}/api/admin/scenarios?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setScenarios(data);
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showInactive]);

  const loadPrograms = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/programs`);
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  useEffect(() => {
    loadScenarios();
    loadPrograms();
  }, [loadScenarios]);

  const handleSave = async (formData) => {
    const method = editingScenario ? 'PUT' : 'POST';
    const url = editingScenario
      ? `${API_BASE}/api/admin/scenarios/${editingScenario.id}`
      : `${API_BASE}/api/admin/scenarios`;

    const response = await fetch(url, {
      method,
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (response.ok) {
      setShowForm(false);
      setEditingScenario(null);
      loadScenarios();
    } else {
      const error = await response.json();
      alert(error.detail || 'Failed to save scenario');
    }
  };

  const handleDelete = async (scenario) => {
    if (!window.confirm(`Delete "${scenario.title}"? This will also delete all nodes and attempts.`)) {
      return;
    }

    const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (response.ok) {
      loadScenarios();
    } else {
      alert('Failed to delete scenario');
    }
  };

  const handleDuplicate = async (scenario) => {
    if (!window.confirm(`Duplicate "${scenario.title}"?`)) {
      return;
    }

    const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}/duplicate`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    if (response.ok) {
      loadScenarios();
    } else {
      alert('Failed to duplicate scenario');
    }
  };

  const toggleActive = async (scenario) => {
    const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ is_active: !scenario.is_active })
    });

    if (response.ok) {
      loadScenarios();
    }
  };

  // Filter scenarios by search
  const filteredScenarios = scenarios.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const beltOptions = getBeltOptions();
  const getBeltName = (slug) => {
    const belt = beltOptions.find(b => b.slug === slug);
    return belt?.name || slug;
  };

  // Show tree editor if a scenario is selected for editing
  if (treeEditScenario) {
    return (
      <ScenarioBuilder
        scenario={treeEditScenario}
        onBack={() => { setTreeEditScenario(null); loadScenarios(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageTitle
        title="Scenario Training"
        description="Create branching-path training scenarios for students"
        icon={<GitBranch className="h-6 w-6" />}
        actions={
          <Button onClick={() => { setEditingScenario(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New Scenario
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search scenarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {SCENARIO_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded"
          />
          Show Inactive
        </label>
      </div>

      {/* Scenario List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filteredScenarios.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
            <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No scenarios found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => { setEditingScenario(null); setShowForm(true); }}
            >
              <Plus className="h-4 w-4 mr-1" /> Create First Scenario
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredScenarios.map(scenario => (
            <Card
              key={scenario.id}
              className={`relative ${!scenario.is_active ? 'opacity-60' : ''}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{scenario.icon_emoji}</span>
                    <div>
                      <CardTitle className="text-base flex items-center gap-1">
                        {scenario.title}
                        {scenario.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </CardTitle>
                      <p className="text-xs text-gray-500 capitalize">
                        {scenario.category?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(scenario)}
                      title={scenario.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {scenario.is_active ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {scenario.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{scenario.description}</p>
                )}

                {/* Stats Row */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span>Difficulty: {scenario.difficulty}/5</span>
                  <span>{scenario.node_count || 0} nodes</span>
                  <span>{scenario.ending_count || 0} endings</span>
                </div>

                {/* Points */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                    +{scenario.base_points} pts
                  </span>
                  <span className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                    +{scenario.optimal_bonus} optimal
                  </span>
                </div>

                {/* Access Control */}
                {(scenario.required_belt || scenario.required_program_ids?.length > 0) && (
                  <div className="flex items-center gap-2 text-xs">
                    <Lock className="h-3 w-3 text-gray-400" />
                    {scenario.required_belt && (
                      <BeltIndicator belt={scenario.required_belt} />
                    )}
                    {scenario.required_program_ids?.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-400">
                        {scenario.required_program_ids.length} program(s)
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTreeEditScenario(scenario)}
                    title="Edit Tree"
                  >
                    <Play className="h-4 w-4 mr-1" /> Edit Tree
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setEditingScenario(scenario); setShowForm(true); }}
                    title="Edit Details"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStatsScenario(scenario)}
                    title="View Stats"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicate(scenario)}
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(scenario)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingScenario(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingScenario ? 'Edit Scenario' : 'New Scenario'}
            </DialogTitle>
          </DialogHeader>
          <ScenarioForm
            scenario={editingScenario}
            programs={programs}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingScenario(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Stats Dialog */}
      <StatsDialog
        scenario={statsScenario}
        open={!!statsScenario}
        onClose={() => setStatsScenario(null)}
      />
    </div>
  );
};

export default ScenarioManager;
